import { ACCOUNTS_MODULES } from './accountsModules';
import { DEM_MODULES } from './demModules';
import { DESIGN_MAIN_MODULES } from './designModules';
import { FORM_MODULES } from './formModules';
import { formatSupabaseError } from './formatSupabaseError';
import type { PortalKind } from './portal';
import type { PortalAccessMap } from './portalAccess';
import { PURCHASING_MAIN_MODULES } from './purchasingModules';
import { REPORT_MODULES } from './reportModules';
import { getDbSupabase } from './supabase';

export type GlobalSearchResultKind = 'page' | 'document';

export type GlobalSearchResult = {
  id: string;
  kind: GlobalSearchResultKind;
  title: string;
  subtitle?: string;
  /** e.g. "DEM › CFF" — shown under the title so duplicates are unambiguous. */
  location: string;
  href: string;
  portal: PortalKind;
  score: number;
};

type PageCatalogEntry = {
  id: string;
  title: string;
  aliases: string[];
  location: string;
  href: string;
  portal: PortalKind;
};

function portalSideOk(map: PortalAccessMap, kind: PortalKind): boolean {
  if (kind === 'crm') return map.crm.hasAccess;
  if (kind === 'it') return map.it.hasAccess;
  if (kind === 'dem') return map.dem.hasAccess;
  if (kind === 'forms') return map.forms.hasAccess;
  if (kind === 'accounts') return map.accounts.hasAccess;
  if (kind === 'report') return map.report.hasAccess;
  if (kind === 'purchasing') return map.purchasing.hasAccess;
  if (kind === 'design') return map.design.hasAccess;
  return false;
}

function normalizeQuery(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

function scoreMatch(query: string, title: string, aliases: string[]): number {
  const q = normalizeQuery(query);
  if (!q) return 0;
  const hay = [title, ...aliases].map((s) => s.toLowerCase());
  let best = 0;
  for (const text of hay) {
    if (text === q) best = Math.max(best, 100);
    else if (text.startsWith(q)) best = Math.max(best, 80);
    else if (text.includes(q)) best = Math.max(best, 60);
    else {
      const parts = q.split(' ').filter(Boolean);
      if (parts.length > 1 && parts.every((p) => text.includes(p))) {
        best = Math.max(best, 55);
      }
    }
  }
  return best;
}

function crmItPages(portal: 'crm' | 'it'): PageCatalogEntry[] {
  const base = `/${portal}`;
  const label = portal === 'it' ? 'IT' : 'CRM';
  const pages: PageCatalogEntry[] = [
    {
      id: `${portal}-dashboard`,
      title: 'Dashboard',
      aliases: ['home', 'main'],
      location: `${label} › Dashboard`,
      href: base,
      portal,
    },
    {
      id: `${portal}-accounts`,
      title: 'Accounts',
      aliases: ['customers', 'clients', 'account'],
      location: `${label} › Accounts`,
      href: `${base}/accounts`,
      portal,
    },
    {
      id: `${portal}-contacts`,
      title: 'Contacts',
      aliases: ['people', 'contact'],
      location: `${label} › Contacts`,
      href: `${base}/contacts`,
      portal,
    },
    {
      id: `${portal}-projects`,
      title: 'Projects',
      aliases: ['project', 'prj'],
      location: `${label} › Projects`,
      href: `${base}/projects`,
      portal,
    },
    {
      id: `${portal}-tasks`,
      title: 'Tasks',
      aliases: ['task', 'todo'],
      location: `${label} › Tasks`,
      href: `${base}/tasks`,
      portal,
    },
    {
      id: `${portal}-tickets`,
      title: 'Tickets',
      aliases: ['ticket', 'tkt', 'support', 'inbox'],
      location: `${label} › Tickets`,
      href: `${base}/tickets`,
      portal,
    },
  ];
  if (portal === 'crm') {
    pages.push(
      {
        id: 'crm-analytics',
        title: 'Analytics',
        aliases: ['reports', 'stats'],
        location: 'CRM › Analytics',
        href: `${base}/analytics`,
        portal,
      },
      {
        id: 'crm-catalogue',
        title: 'Catalogue',
        aliases: ['catalog', 'products'],
        location: 'CRM › Catalogue',
        href: `${base}/catalogue`,
        portal,
      },
      {
        id: 'crm-open-data',
        title: 'Open Data',
        aliases: ['data.gov', 'acra', 'data gov'],
        location: 'CRM › Open Data',
        href: `${base}/data-gov-sg`,
        portal,
      },
    );
  }
  return pages;
}

export function buildPageCatalog(access: PortalAccessMap): PageCatalogEntry[] {
  const pages: PageCatalogEntry[] = [];

  if (portalSideOk(access, 'crm')) pages.push(...crmItPages('crm'));
  if (portalSideOk(access, 'it')) pages.push(...crmItPages('it'));

  if (portalSideOk(access, 'dem')) {
    for (const mod of DEM_MODULES) {
      if (mod.navHidden && mod.admin) continue;
      pages.push({
        id: `dem-${mod.slug}`,
        title: mod.title,
        aliases: [
          mod.label,
          mod.slug.replace(/-/g, ' '),
          ...(mod.slug === 'customer-feedback' ? ['cff', 'feedback', 'ncpar'] : []),
          ...(mod.slug === 'pricing-update' ? ['puf', 'pricing', 'price update'] : []),
        ],
        location: `DEM › ${mod.label}`,
        href: `/dem/${mod.slug}`,
        portal: 'dem',
      });
    }
  }

  if (portalSideOk(access, 'forms')) {
    for (const mod of FORM_MODULES) {
      pages.push({
        id: `forms-${mod.slug}`,
        title: mod.title,
        aliases: [
          mod.label,
          mod.slug.replace(/-/g, ' '),
          ...(mod.slug === 'customer-feedback' ? ['cff', 'feedback', 'ncpar'] : []),
          ...(mod.slug === 'certificate-request' ? ['ctr', 'certificate'] : []),
          ...(mod.slug === 'internal-memo' ? ['memo'] : []),
          ...(mod.slug === 'customise-items-transfer' ? ['cit', 'transfer'] : []),
          ...(mod.slug === 'pricing-update' ? ['puf', 'pricing', 'price update'] : []),
        ],
        location: `Forms › ${mod.label}`,
        href: `/forms/${mod.slug}`,
        portal: 'forms',
      });
    }
  }

  if (portalSideOk(access, 'accounts')) {
    for (const mod of ACCOUNTS_MODULES) {
      pages.push({
        id: `accounts-${mod.slug}`,
        title: mod.title,
        aliases: [mod.label, 'ar', 'collection', 'aged'],
        location: `Accounts › ${mod.label}`,
        href: `/accounts/${mod.slug}`,
        portal: 'accounts',
      });
    }
  }

  if (portalSideOk(access, 'report')) {
    for (const mod of REPORT_MODULES) {
      pages.push({
        id: `report-${mod.slug}`,
        title: mod.title,
        aliases: [mod.label, mod.slug.replace(/-/g, ' ')],
        location: `Report › ${mod.label}`,
        href: `/report/${mod.slug}`,
        portal: 'report',
      });
    }
  }

  if (portalSideOk(access, 'purchasing')) {
    for (const mod of PURCHASING_MAIN_MODULES) {
      pages.push({
        id: `purchasing-${mod.slug}`,
        title: mod.title,
        aliases: [mod.label, mod.slug.replace(/-/g, ' ')],
        location: `Purchasing › ${mod.label}`,
        href: `/purchasing/${mod.slug}`,
        portal: 'purchasing',
      });
    }
  }

  if (portalSideOk(access, 'design')) {
    for (const mod of DESIGN_MAIN_MODULES) {
      pages.push({
        id: `design-${mod.slug}`,
        title: mod.title,
        aliases: [mod.label, 'catalogue', 'promo'],
        location: `Design › ${mod.label}`,
        href: `/design/${mod.slug}`,
        portal: 'design',
      });
    }
  }

  // Calendar is shared — always searchable when signed in.
  pages.push({
    id: 'calendar',
    title: 'Calendar',
    aliases: ['schedule', 'events'],
    location: 'Portal › Calendar',
    href: '/calendar',
    portal: 'crm',
  });

  return pages;
}

export function searchPages(access: PortalAccessMap, query: string): GlobalSearchResult[] {
  const q = normalizeQuery(query);
  if (q.length < 1) return [];
  const out: GlobalSearchResult[] = [];
  for (const page of buildPageCatalog(access)) {
    const score = scoreMatch(q, page.title, page.aliases);
    if (score <= 0) continue;
    out.push({
      id: page.id,
      kind: 'page',
      title: page.title,
      location: page.location,
      href: page.href,
      portal: page.portal,
      score,
    });
  }
  return out.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title)).slice(0, 12);
}

function escapeIlike(value: string): string {
  return value.replace(/[%_]/g, '\\$&');
}

async function searchCffDocuments(
  access: PortalAccessMap,
  query: string,
): Promise<GlobalSearchResult[]> {
  if (!portalSideOk(access, 'dem') && !portalSideOk(access, 'forms')) return [];
  const q = query.trim();
  if (q.length < 2) return [];

  const db = getDbSupabase('forms');
  const escaped = escapeIlike(q);
  let builder = db
    .from('customer_feedback_submissions')
    .select('id, application_no, company_name, status, submitted_at')
    .order('submitted_at', { ascending: false })
    .limit(8);

  const exactCff = /^CFF-\d{4}-\d{2}-\d{1,3}$/i.test(q);
  if (exactCff) {
    builder = builder.ilike('application_no', q);
  } else if (/^CFF/i.test(q)) {
    builder = builder.ilike('application_no', `${escaped}%`);
  } else {
    builder = builder.or(
      `application_no.ilike.%${escaped}%,company_name.ilike.%${escaped}%`,
    );
  }

  const { data, error } = await builder;
  if (error) throw new Error(formatSupabaseError(error));

  const preferDem = portalSideOk(access, 'dem');
  const portal: PortalKind = preferDem ? 'dem' : 'forms';
  const base = preferDem ? '/dem/customer-feedback' : '/forms/customer-feedback';
  const location = preferDem ? 'DEM › CFF' : 'Forms › Customer Feedback Form';

  return (data ?? []).map((row, index) => {
    const r = row as {
      id: string;
      application_no?: string;
      company_name?: string;
      status?: string;
    };
    const appNo = String(r.application_no ?? '').trim() || 'CFF';
    const exact = exactCff && appNo.toLowerCase() === q.toLowerCase();
    return {
      id: `cff-${r.id}`,
      kind: 'document' as const,
      title: appNo,
      subtitle: [r.company_name, r.status].filter(Boolean).join(' · ') || undefined,
      location,
      href: `${base}/${r.id}`,
      portal,
      score: (exact ? 100 : /^CFF/i.test(q) ? 92 : 78) - index,
    };
  });
}

async function searchMemoDocuments(
  access: PortalAccessMap,
  query: string,
): Promise<GlobalSearchResult[]> {
  if (!portalSideOk(access, 'forms')) return [];
  const q = query.trim();
  if (q.length < 2) return [];

  const db = getDbSupabase('forms');
  const escaped = escapeIlike(q);
  const looksLikeMemoRef = /memo/i.test(q) || /\d{4}\/ADM\/MEMO\//i.test(q);

  let builder = db
    .from('memo_submissions')
    .select('id, application_no, company_name, status, submitted_at')
    .order('submitted_at', { ascending: false })
    .limit(8);

  if (/\d{4}\/ADM\/MEMO\/\d{2}\/\d{1,4}$/i.test(q)) {
    builder = builder.ilike('application_no', q);
  } else if (looksLikeMemoRef) {
    builder = builder.ilike('application_no', `%${escaped}%`);
  } else {
    builder = builder.or(
      `application_no.ilike.%${escaped}%,company_name.ilike.%${escaped}%`,
    );
  }

  const { data, error } = await builder;
  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? []).map((row, index) => {
    const r = row as {
      id: string;
      application_no?: string;
      company_name?: string;
      status?: string;
    };
    const appNo = String(r.application_no ?? '').trim() || 'Memo';
    const exact = appNo.toLowerCase() === q.toLowerCase();
    return {
      id: `memo-${r.id}`,
      kind: 'document' as const,
      title: appNo,
      subtitle: [r.company_name, r.status].filter(Boolean).join(' · ') || undefined,
      location: 'Forms › Internal Memo',
      href: `/forms/internal-memo/${r.id}`,
      portal: 'forms' as const,
      score: (exact ? 100 : looksLikeMemoRef ? 90 : 70) - index,
    };
  });
}

async function searchCitDocuments(
  access: PortalAccessMap,
  query: string,
): Promise<GlobalSearchResult[]> {
  if (!portalSideOk(access, 'forms')) return [];
  const q = query.trim();
  // No application_no on CIT — avoid noisy hits on short generic queries.
  const citIntent = /\b(cit|customise|customize|transfer)\b/i.test(q);
  if (q.length < 3 && !citIntent) return [];
  if (q.length < 2) return [];

  const db = getDbSupabase('forms');
  const escaped = escapeIlike(q);
  const { data, error } = await db
    .from('cit_submissions')
    .select('id, company_name, status, submitted_at')
    .or(`company_name.ilike.%${escaped}%,status.ilike.%${escaped}%`)
    .order('submitted_at', { ascending: false })
    .limit(8);
  if (error) throw new Error(formatSupabaseError(error));

  return (data ?? []).map((row, index) => {
    const r = row as { id: string; company_name?: string; status?: string };
    const name = String(r.company_name ?? '').trim() || 'Sales Confirmation';
    return {
      id: `cit-${r.id}`,
      kind: 'document' as const,
      title: name,
      subtitle: r.status ? String(r.status) : undefined,
      location: 'Forms › Sales Confirmation',
      href: `/forms/customise-items-transfer/${r.id}`,
      portal: 'forms' as const,
      score: (citIntent ? 72 : 64) - index,
    };
  });
}

async function searchTicketsDocuments(
  access: PortalAccessMap,
  query: string,
): Promise<GlobalSearchResult[]> {
  const portals: Array<'crm' | 'it'> = [];
  if (portalSideOk(access, 'crm')) portals.push('crm');
  if (portalSideOk(access, 'it')) portals.push('it');
  if (portals.length === 0) return [];

  const q = query.trim();
  if (q.length < 2) return [];
  const escaped = escapeIlike(q);
  const out: GlobalSearchResult[] = [];

  for (const portal of portals) {
    const db = getDbSupabase(portal);
    const { data, error } = await db
      .from('tickets')
      .select('id, ticket_number, subject, status')
      .or(
        `ticket_number.ilike.%${escaped}%,subject.ilike.%${escaped}%`,
      )
      .order('updated_at', { ascending: false })
      .limit(6);
    if (error) continue;
    const label = portal === 'it' ? 'IT' : 'CRM';
    for (const [index, row] of (data ?? []).entries()) {
      const r = row as {
        id: string;
        ticket_number?: string;
        subject?: string;
        status?: string;
      };
      const number = String(r.ticket_number ?? '').trim() || 'Ticket';
      const exactTicket = number.toLowerCase() === q.toLowerCase();
      out.push({
        id: `ticket-${portal}-${r.id}`,
        kind: 'document',
        title: number,
        subtitle: [r.subject, r.status].filter(Boolean).join(' · ') || undefined,
        location: `${label} › Tickets`,
        href: `/${portal}/tickets/${r.id}`,
        portal,
        score: (exactTicket ? 100 : /^TKT|^BTH-ENQ/i.test(q) ? 92 : 70) - index,
      });
    }
  }

  return out;
}

async function searchProjectDocuments(
  access: PortalAccessMap,
  query: string,
): Promise<GlobalSearchResult[]> {
  const portals: Array<'crm' | 'it'> = [];
  if (portalSideOk(access, 'crm')) portals.push('crm');
  if (portalSideOk(access, 'it')) portals.push('it');
  if (portals.length === 0) return [];

  const q = query.trim();
  if (q.length < 2) return [];
  const escaped = escapeIlike(q);
  const out: GlobalSearchResult[] = [];

  for (const portal of portals) {
    const db = getDbSupabase(portal);
    const { data, error } = await db
      .from('projects')
      .select('id, reference_no, title, status')
      .or(`reference_no.ilike.%${escaped}%,title.ilike.%${escaped}%`)
      .order('updated_at', { ascending: false })
      .limit(6);
    if (error) continue;
    const label = portal === 'it' ? 'IT' : 'CRM';
    for (const [index, row] of (data ?? []).entries()) {
      const r = row as {
        id: string;
        reference_no?: string;
        title?: string;
        status?: string;
      };
      const ref = String(r.reference_no ?? '').trim();
      const exactPrj = Boolean(ref) && ref.toLowerCase() === q.toLowerCase();
      out.push({
        id: `project-${portal}-${r.id}`,
        kind: 'document',
        title: ref || String(r.title ?? 'Project'),
        subtitle: [ref ? r.title : null, r.status].filter(Boolean).join(' · ') || undefined,
        location: `${label} › Projects`,
        href: ref
          ? `/${portal}/projects/${encodeURIComponent(ref)}`
          : `/${portal}/projects/id/${r.id}`,
        portal,
        score: (exactPrj ? 100 : /^PRJ/i.test(q) ? 92 : 68) - index,
      });
    }
  }

  return out;
}

async function searchAccountDocuments(
  access: PortalAccessMap,
  query: string,
): Promise<GlobalSearchResult[]> {
  const portals: Array<'crm' | 'it'> = [];
  if (portalSideOk(access, 'crm')) portals.push('crm');
  if (portalSideOk(access, 'it')) portals.push('it');
  if (portals.length === 0) return [];

  const q = query.trim();
  if (q.length < 2) return [];
  const escaped = escapeIlike(q);
  const out: GlobalSearchResult[] = [];

  for (const portal of portals) {
    const db = getDbSupabase(portal);
    const { data, error } = await db
      .from('accounts')
      .select('id, code, name')
      .or(`code.ilike.%${escaped}%,name.ilike.%${escaped}%`)
      .order('code', { ascending: true })
      .limit(6);
    if (error) continue;
    const label = portal === 'it' ? 'IT' : 'CRM';
    for (const [index, row] of (data ?? []).entries()) {
      const r = row as { id?: string; code?: string; name?: string };
      const code = String(r.code ?? '').trim();
      if (!code) continue;
      const exactCode = code.toLowerCase() === q.toLowerCase();
      out.push({
        id: `account-${portal}-${code}`,
        kind: 'document',
        title: code,
        subtitle: String(r.name ?? '').trim() || undefined,
        location: `${label} › Accounts`,
        href: `/${portal}/accounts/${encodeURIComponent(code)}`,
        portal,
        score: (exactCode ? 100 : 65) - index,
      });
    }
  }

  return out;
}

/** Live document hits (permission-filtered). Safe to call with empty query → []. */
export async function searchDocuments(
  access: PortalAccessMap,
  query: string,
): Promise<GlobalSearchResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const settled = await Promise.allSettled([
    searchCffDocuments(access, q),
    searchMemoDocuments(access, q),
    searchCitDocuments(access, q),
    searchTicketsDocuments(access, q),
    searchProjectDocuments(access, q),
    searchAccountDocuments(access, q),
  ]);

  const out: GlobalSearchResult[] = [];
  for (const result of settled) {
    if (result.status === 'fulfilled') out.push(...result.value);
  }

  return out.sort((a, b) => b.score - a.score || a.title.localeCompare(b.title)).slice(0, 16);
}

export function mergeSearchResults(
  pages: GlobalSearchResult[],
  documents: GlobalSearchResult[],
): { pages: GlobalSearchResult[]; documents: GlobalSearchResult[] } {
  return {
    pages: pages.slice(0, 8),
    documents: documents.slice(0, 10),
  };
}
